import { Element } from "@xmpp/xml"
import MarkerParser from "./markerParser"
import SettingsParser from "./settingsParser"
import SubjectParser from "./subjectParser"
import RouteParser from "./routeParser"
import ActivityParser from "./activityParser"
import BookmarkEventParser from "./bookmarkParser"
import RetractionParser from "./retractionParser"
import MucInvitationParser from "./mucInvitationParser"
import PinnedMessageEventParser from "./pinnedMessageParser"
import { TachoPubSubParser } from "./tachoPubSubParser"
import { DeliveryOrderExportStatusPubSubParser } from "./deliveryOrderExportStatusPubSubParser"
import { ReferenceRouteStatusPubSubParser } from "./referenceRouteStatusPubSubParser"
import { LocationPubSubParser } from "@/lib/locationPubSubParser"
import { TrailerLocationPubSubParser } from "@/lib/trailerLocationPubSubParser"
import { Route2gPubSubParser } from "@/lib/route2gPubSubParser"
import { RouteAlertConfigPubSubParser } from "./routeAlertConfigPubSubParser"
import { UserVcardPubSubParser } from "@/lib/userVcardPubSubParser"
import { CARS_NODE } from "@/lib/carsParser"
import { WORKFLOWS_NODE } from "@/lib/openclawWorkflowsParser"
import { CHAT_SETTINGS_NODE } from "@/api/xeps/ChatsSettings"
import { GPS_CONFIG_NODE } from "@/api/xeps/GpsConfig"
import type { Route } from "@/reducers/routesSlice"
import type { Activity } from "@/reducers/activitySlice"
import type { MessageRetraction } from "@/reducers/messagesSlice"
import type { Subject } from "@/reducers/subjectsSlice"
import type { Settings } from "@/reducers/settingsSlice"
import type { DriverLocation } from "@/reducers/locationsSlice"
import type { TrailerLocation } from "@/reducers/trailerLocationsSlice"
import type { BookmarkEvent } from "./bookmarkParser"
import type { PinnedMessageEvent } from "./pinnedMessageParser"
import type { Route2g } from "@/reducers/routes2gSlice"
import type { Tacho } from "@/reducers/tachosSlice"
import type { DeliveryOrderExportStatus } from "@/reducers/deliveryOrderExportStatusesSlice"
import type { Profile } from "@/reducers/profilesSlice"
import type { ReferenceRouteStatus } from "@/reducers/referenceRouteStatusesSlice"
import type { RouteAlertConfigWithJid } from "@/api/xeps/RouteAlertConfigs"
import type { MucInvitation } from "./mucInvitationParser"
import type { PubSubEvent } from "@/lib/PubSubEventParser"
import { parseAiWidgetMap, type MapViewData } from "@/lib/aiWidgetMapParser"

type UnparsedStanzaType =
  | "presence"
  | "message"
  | "private_message"
  | "private_chat_state"
  | "chat_state"
  | "chat_marker"
  | "archived_chat_marker"
  | "archived_message"
  | "archived_reaction"
  | "reaction"
  | "message_file_shared"
  | "archived_message_file_shared"
  | "extracted_metadata"
  | "archived_extracted_metadata"
  | "document_driver_context"
  | "archived_document_driver_context"
  | "document_link"
  | "archived_document_link"
  | "fasten"
  | "archived_fasten"
  | "remove_route"
  | "communicator_bookmark"
  | "chat_settings_pubsub_event"
  | "gps_config_pubsub_event"
  | "car_pubsub_event"
  | "workflow_pubsub_event"
  | "rtt"

export type StanzaParseResult =
  | { stanza: Element; type: UnparsedStanzaType }
  | { stanza: Element; type: "route"; parsed: Route }
  | { stanza: Element; type: "activity"; parsed: Activity }
  | { stanza: Element; type: "retraction"; parsed: MessageRetraction }
  | { stanza: Element; type: "archived_retraction"; parsed: MessageRetraction }
  | { stanza: Element; type: "subject"; parsed: Subject }
  | { stanza: Element; type: "geoloc"; parsed: PubSubEvent<DriverLocation> }
  | {
      stanza: Element
      type: "trailer_geoloc"
      parsed: PubSubEvent<TrailerLocation>
    }
  | { stanza: Element; type: "settings"; parsed: Partial<Settings> }
  | { stanza: Element; type: "bookmark_event"; parsed: BookmarkEvent }
  | {
      stanza: Element
      type: "pinned_message_event"
      parsed: PinnedMessageEvent
    }
  | { stanza: Element; type: "route2g"; parsed: PubSubEvent<Route2g> }
  | { stanza: Element; type: "tacho"; parsed: PubSubEvent<Tacho> }
  | {
      stanza: Element
      type: "delivery_order_export_status"
      parsed: PubSubEvent<DeliveryOrderExportStatus>
    }
  | { stanza: Element; type: "user_vcard"; parsed: PubSubEvent<Profile> }
  | {
      stanza: Element
      type: "reference_route_status"
      parsed: PubSubEvent<ReferenceRouteStatus>
    }
  | {
      stanza: Element
      type: "route_alert_config"
      parsed: PubSubEvent<RouteAlertConfigWithJid>
    }
  | { stanza: Element; type: "muc_invitation"; parsed: MucInvitation }
  | { stanza: Element; type: "ai_widget_map"; parsed: MapViewData }

export type StanzaType = StanzaParseResult["type"]

export const parseStanza = (
  stanza: Element,
  jid?: string,
): StanzaParseResult | null => {
  if (stanza.is("presence"))
    return { stanza: enrichTo(stanza, jid), type: "presence" }

  if (!stanza.is("message")) return null

  // Private message
  if (stanza.attrs.type === "chat") {
    if (isChatStateMessage(stanza)) {
      if (stanza.attrs.type === "chat") {
        return { stanza: enrichTo(stanza, jid), type: "private_chat_state" }
      }
    }
    return { stanza: enrichTo(stanza, jid), type: "private_message" }
  }

  if (stanza.getChildText("delay") === "Offline Storage") {
    console.warn("Ignoring stanza from Offline Storage")
    return null
  }

  const archivedMessage = archiveQueryResult(stanza)

  if (archivedMessage) {
    const archivedRetraction = RetractionParser.parse(
      enrichTo(archivedMessage, jid),
      parseArchivedTimestamp(stanza),
    )
    if (archivedRetraction) {
      return {
        stanza: enrichTo(archivedMessage, jid),
        type: "archived_retraction",
        parsed: archivedRetraction,
      }
    }
  }

  if (archivedMessage && hasChatMarker(archivedMessage)) {
    return {
      stanza: enrichTo(archivedMessage, jid),
      type: "archived_chat_marker",
    }
  }

  // Check ai-widget-map inside the archived envelope BEFORE the plain
  // archived_message branch — otherwise MAM replay would strip the
  // aiWidgetMap field by routing the message through the generic
  // archived_message path.
  if (archivedMessage) {
    const widgetMap = parseAiWidgetMap(
      enrichTo(archivedMessage, jid),
      archivedMessage.attrs.from?.split("/")[0] ?? "",
      archivedMessage.attrs.id ?? "",
      parseArchivedTimestamp(stanza),
    )
    if (widgetMap) {
      return {
        stanza: enrichTo(archivedMessage, jid),
        type: "ai_widget_map",
        parsed: widgetMap,
      }
    }
  }

  if (
    archivedMessage &&
    (hasBody(archivedMessage) || isFile(archivedMessage))
  ) {
    return { stanza: enrichTo(archivedMessage, jid), type: "archived_message" }
  }

  if (archivedMessage && hasReactions(archivedMessage)) {
    return { stanza: enrichTo(archivedMessage, jid), type: "archived_reaction" }
  }

  if (hasReactions(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "reaction" }
  }

  if (hasMessageFileShared(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "message_file_shared" }
  }

  if (archivedMessage && hasMessageFileShared(archivedMessage)) {
    return {
      stanza: enrichTo(archivedMessage, jid),
      type: "archived_message_file_shared",
    }
  }

  if (hasExtractedMetadata(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "extracted_metadata" }
  }

  if (archivedMessage && hasExtractedMetadata(archivedMessage)) {
    return {
      stanza: enrichTo(archivedMessage, jid),
      type: "archived_extracted_metadata",
    }
  }

  if (hasDocumentDriverContext(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "document_driver_context" }
  }

  if (archivedMessage && hasDocumentDriverContext(archivedMessage)) {
    return {
      stanza: enrichTo(archivedMessage, jid),
      type: "archived_document_driver_context",
    }
  }

  if (hasDocumentLink(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "document_link" }
  }

  if (archivedMessage && hasDocumentLink(archivedMessage)) {
    return {
      stanza: enrichTo(archivedMessage, jid),
      type: "archived_document_link",
    }
  }

  if (hasFasten(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "fasten" }
  }

  if (archivedMessage && hasFasten(archivedMessage)) {
    return { stanza: enrichTo(archivedMessage, jid), type: "archived_fasten" }
  }

  {
    const retraction = RetractionParser.parse(enrichTo(stanza, jid))
    if (retraction) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "retraction",
        parsed: retraction,
      }
    }
  }

  {
    const subject = SubjectParser.parse(enrichTo(stanza, jid))
    if (subject) {
      return { stanza: enrichTo(stanza, jid), type: "subject", parsed: subject }
    }
  }

  if (hasChatMarker(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "chat_marker" }
  }

  {
    const geoloc = LocationPubSubParser.parse(enrichTo(stanza, jid))
    if (geoloc) {
      return { stanza: enrichTo(stanza, jid), type: "geoloc", parsed: geoloc }
    }
  }

  {
    const trailerGeoloc = TrailerLocationPubSubParser.parse(
      enrichTo(stanza, jid),
    )
    if (trailerGeoloc) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "trailer_geoloc",
        parsed: trailerGeoloc,
      }
    }
  }

  {
    const settings = SettingsParser.parseSettings(enrichTo(stanza, jid))
    if (settings !== undefined) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "settings",
        parsed: settings,
      }
    }
  }

  if (RouteParser.shouldRemoveRoute(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "remove_route" }
  }

  {
    const route = RouteParser.parse(enrichTo(stanza, jid))
    if (route) {
      return { stanza: enrichTo(stanza, jid), type: "route", parsed: route }
    }
  }

  {
    const activity = ActivityParser.parse(enrichTo(stanza, jid))
    if (activity) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "activity",
        parsed: activity,
      }
    }
  }

  {
    const pinned = PinnedMessageEventParser.parse(enrichTo(stanza, jid))
    if (pinned) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "pinned_message_event",
        parsed: pinned,
      }
    }
  }

  {
    const widgetMap = parseAiWidgetMap(
      enrichTo(stanza, jid),
      stanza.attrs.from?.split("/")[0] ?? "",
      stanza.attrs.id ?? "",
      parseArchivedTimestamp(stanza),
    )
    if (widgetMap) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "ai_widget_map",
        parsed: widgetMap,
      }
    }
  }

  if (hasBody(stanza) || isFile(stanza))
    return { stanza: enrichTo(stanza, jid), type: "message" }

  // XEP-0301 Real-Time Text
  if (stanza.getChild("rtt", "urn:xmpp:rtt:0")) {
    return { stanza: enrichTo(stanza, jid), type: "rtt" }
  }

  if (isChatStateMessage(stanza)) {
    return { stanza: enrichTo(stanza, jid), type: "chat_state" }
  }

  {
    const bookmark = BookmarkEventParser.parse(enrichTo(stanza, jid))
    if (bookmark) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "bookmark_event",
        parsed: bookmark,
      }
    }
  }

  // Cheap XML checks for pubsub event types that use different parsers in handlers
  if (stanza.getChild("event")?.getChildByAttr("node", CHAT_SETTINGS_NODE)) {
    return { stanza: enrichTo(stanza, jid), type: "chat_settings_pubsub_event" }
  }

  if (stanza.getChild("event")?.getChildByAttr("node", GPS_CONFIG_NODE)) {
    return { stanza: enrichTo(stanza, jid), type: "gps_config_pubsub_event" }
  }

  if (stanza.getChild("event")?.getChildByAttr("node", CARS_NODE)) {
    return { stanza: enrichTo(stanza, jid), type: "car_pubsub_event" }
  }

  if (stanza.getChild("event")?.getChildByAttr("node", WORKFLOWS_NODE)) {
    return { stanza: enrichTo(stanza, jid), type: "workflow_pubsub_event" }
  }

  {
    const route2g = Route2gPubSubParser.parse(enrichTo(stanza, jid))
    if (route2g) {
      return { stanza: enrichTo(stanza, jid), type: "route2g", parsed: route2g }
    }
  }

  {
    const tacho = TachoPubSubParser.parse(enrichTo(stanza, jid))
    if (tacho) {
      return { stanza: enrichTo(stanza, jid), type: "tacho", parsed: tacho }
    }
  }

  {
    const exportStatus = DeliveryOrderExportStatusPubSubParser.parse(
      enrichTo(stanza, jid),
    )
    if (exportStatus) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "delivery_order_export_status",
        parsed: exportStatus,
      }
    }
  }

  {
    const user = UserVcardPubSubParser.parse(enrichTo(stanza, jid))
    if (user) {
      return { stanza: enrichTo(stanza, jid), type: "user_vcard", parsed: user }
    }
  }

  {
    const routeStatus = ReferenceRouteStatusPubSubParser.parse(
      enrichTo(stanza, jid),
    )
    if (routeStatus) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "reference_route_status",
        parsed: routeStatus,
      }
    }
  }

  {
    const alertConfig = RouteAlertConfigPubSubParser.parse(
      enrichTo(stanza, jid),
    )
    if (alertConfig) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "route_alert_config",
        parsed: alertConfig,
      }
    }
  }

  {
    const invitation = MucInvitationParser.parse(enrichTo(stanza, jid))
    if (invitation) {
      return {
        stanza: enrichTo(stanza, jid),
        type: "muc_invitation",
        parsed: invitation,
      }
    }
  }

  return null
}

export const isFile = (stanza: Element) => {
  return (
    stanza.getChildText("url") ||
    stanza.getChildByAttr("xmlns", "urn:xmpp:sims:1", "urn:xmpp:sims:1", true)
  )
}

const isChatStateMessage = (stanza: Element) => {
  return (
    stanza.getChild("composing") ||
    stanza.getChild("paused") ||
    stanza.getChild("active")
  )
}

const hasReactions = (stanza: Element) => {
  return stanza.getChild("reactions") !== undefined
}

const hasMessageFileShared = (stanza: Element) => {
  return stanza.getChild("message-file-shared") !== undefined
}

const hasExtractedMetadata = (stanza: Element) => {
  return stanza.getChild("extracted-metadata") !== undefined
}

const hasDocumentDriverContext = (stanza: Element) => {
  return stanza.getChild("document-driver-context") !== undefined
}

const hasDocumentLink = (stanza: Element) => {
  return stanza.getChild("document-link") !== undefined
}

const hasFasten = (stanza: Element) => {
  return stanza.getChildByAttr("xmlns", "urn:xmpp:fasten:0")
}

export const hasBody = (stanza: Element) => {
  const body = stanza.getChildText("body")

  return body && body.length > 0
}

const hasChatMarker = (stanza: Element) => {
  return (
    MarkerParser.getChatMarker("received", stanza) ||
    MarkerParser.getChatMarker("displayed", stanza)
  )
}

export const archiveQueryResult = (stanza: Element): Element | undefined => {
  return stanza.getChild("result")?.getChild("forwarded")?.getChild("message")
}

export const enrichTo = (stanza: Element, jid?: string): Element => {
  if (stanza.attrs.to || !jid) return stanza

  stanza.attrs.to = jid
  return stanza
}

export const parseArchivedTimestamp = (stanza: Element): string => {
  const delayed = stanza
    .getChild("result")
    ?.getChild("forwarded")
    ?.getChild("delay")

  if (!delayed) return new Date().toISOString()

  return delayed.attrs.stamp
}
